import { Form } from "antd/es";
import * as React from "react";
import confJson from "../../shared/data/conf.json";
import { IWithContextProp, withContext } from "../../shared/hoc/with-context";
import { OnPressKeyRules } from "../../shared/utility/on-press-key-rules";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import KuikaInput from "../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../ui-kit/typography/H5/H5";
import "./delete-modal.scss";
import KuikaText from "../ui-kit/typography/Text/Text";

const domainConf = confJson.domainConfigs;

export interface IDeleteModalState {
  isLoading: boolean;
  inputValue: string;
}

export interface IDeleteModalProps {
  itemName: string;
  extraText?: string | React.ReactNode;
  triggerModal: boolean;
  closeModal: any;
  deleteItem: any;
  deleteType: string;
  type?: "delete" | "cancel";
}

class DeleteModal extends React.Component<IDeleteModalProps & IWithContextProp, IDeleteModalState> {
  static defaultProps: Partial<IDeleteModalProps> = {
    type: "delete"
  };

  constructor(props: IDeleteModalProps & IWithContextProp) {
    super(props);
    this.state = {
      isLoading: false,
      inputValue: ""
    };
  }

  componentDidUpdate(prevProps: IDeleteModalProps) {
    if (!prevProps.triggerModal && this.props.triggerModal) {
      setTimeout(() => {
        const elements = document.querySelectorAll(".kuika-delete-modal__form-item input");
        if (elements && elements.length > 0) {
          (elements[0] as HTMLInputElement).focus();
        }
      }, 300);
    }
  }

  getExpectedWord = (): string => (this.props.type === "cancel" ? "cancel" : "delete");

  isInputMatchingExpected = (): boolean => this.state.inputValue.toLowerCase() === this.getExpectedWord();

  deleteItem = async () => {
    if (!this.isInputMatchingExpected()) {
      return;
    }

    this.setState({ isLoading: true });
    await this.props.deleteItem();
    setTimeout(() => {
      this.props.closeModal();
      this.setState({ isLoading: false, inputValue: "" });
    }, 200);
  };

  onFinishFailed = (errorInfo) => {};

  handleFormSubmit = () => {
    if (this.isInputMatchingExpected()) {
      this.deleteItem();
    }
  };

  handleInputChange = (e) => {
    this.setState({ inputValue: e.target.value });
  };

  handleOnPressKey = (e) => {
    const ch = e.which;
    if (ch === 13) {
      e.preventDefault();
      if (this.isInputMatchingExpected()) {
        this.deleteItem();
      }
      return;
    }

    if (this.props.deleteType === "component group") {
      return OnPressKeyRules.handlePressTagNameKey(e);
    }
  };

  closeModal = () => {
    this.props.closeModal();
    this.setState({ inputValue: "" });
  };

  renderElement() {
    const expectedWord = this.getExpectedWord();
    const actionLabel = expectedWord === "cancel" ? "Cancel" : "Delete";
    const isConfirmDisabled = this.state.isLoading || !this.isInputMatchingExpected();
    const descriptionText = (
      <>
        Are you sure you want to {expectedWord} the &quot;{this.props.itemName}&quot; {this.props.extraText ?? ""}{" "}
        Please type &quot;{expectedWord}&quot; down below to continue
      </>
    );

    const footer = (
      <KModalFooter
        onCancel={this.closeModal}
        cancelLabel={this.props.type === "cancel" ? "Back" : "Cancel"}
        cancelTestId="closeDeleteModalButton"
        cancelOnboardingId="common_delete-modal_close"
        submitLabel={this.props.type === "cancel" ? "Confirm" : actionLabel}
        submitTone="danger"
        submitTestId="deleteModalDeleteButton"
        submitOnboardingId="common_delete-modal_delete"
        onSubmit={this.deleteItem}
        loading={this.state.isLoading}
        disabled={isConfirmDisabled}
      />
    );

    return (
      <KModal
        showModal={this.props.triggerModal}
        closeModal={this.closeModal}
        header={`${actionLabel} ${this.props.deleteType}`}
        footer={footer}
        width={400}
        centered
        isLoading={this.state.isLoading}
      >
        <div className="kuika-delete-modal">
          <div className="kuika-delete-modal__header">
            <KuikaModalIconWrapper iconName={this.props.type === "cancel" ? "x" : "trash"} intent="destructive" />
            <div className="kuika-delete-modal__title">
              <KuikaH5 data-testid="deleteModalTitle" data-onboarding-id="common_delete-modal_title">
                {actionLabel} {this.props.deleteType}
              </KuikaH5>
            </div>
          </div>
          <div className="kuika-delete-modal__content">
            <div className="kuika-delete-modal__description">
              <KuikaText>{descriptionText}</KuikaText>
            </div>

            <div className="kuika-delete-modal__form">
              <div className="kuika-delete-modal__form-item">
                <Form name="basic" onFinish={this.handleFormSubmit} onFinishFailed={this.onFinishFailed}>
                  <KuikaInput
                    data-testid="deleteModalInput"
                    data-onboarding-id="common_delete-modal_input"
                    onKeyDown={this.handleOnPressKey}
                    onChange={this.handleInputChange}
                    value={this.state.inputValue}
                    autoFocus
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
      </KModal>
    );
  }

  render() {
    return <>{this.renderElement()}</>;
  }
}

const deleteModal = withContext<IDeleteModalProps>(DeleteModal);
export { deleteModal as DeleteModal };
